import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { ArrowLeft, Copy, ExternalLink } from "lucide-react";
import { toast } from "../../components/ui/use-toast";
import { getAuth } from "firebase/auth";

interface GTFOBinsFunction {
  name: string;
  description: string;
  code: string;
  sudo: boolean;
  suid: boolean;
  capabilities: boolean;
  limited_suid: boolean;
  type: string;
}

interface GTFOBinsBinary {
  name: string;
  description: string;
  functions: GTFOBinsFunction[];
  url: string;
}

export const BinaryDetailPage: React.FC = () => {
  const { binaryName } = useParams<{ binaryName: string }>();
  const navigate = useNavigate();
  const auth = getAuth();
  const [binary, setBinary] = useState<GTFOBinsBinary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const handleSignOut = async () => {
    try {
      await auth.signOut();
      navigate('/signin');
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  useEffect(() => {
    const loadBinaryData = async () => {
      try {
        setLoading(true);
        const response = await fetch('/gtfobins.json');
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        const foundBinary = data.find((b: GTFOBinsBinary) => b.name === binaryName);
        
        if (!foundBinary) {
          setError(`Binary "${binaryName}" not found`);
          return;
        }
        
        setBinary(foundBinary);
      } catch (error) {
        console.error("Error loading binary data:", error);
        setError("Failed to load binary data");
      } finally {
        setLoading(false);
      }
    };

    if (binaryName) {
      loadBinaryData();
    }
  }, [binaryName]);

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: "Copied!",
      description: "Command copied to clipboard",
    });
  };

  const getFunctionTypeBadge = (functionType: string) => {
    const typeColors: { [key: string]: string } = {
      'shell': 'bg-green-100 text-green-800',
      'sudo': 'bg-red-100 text-red-800',
      'suid': 'bg-purple-100 text-purple-800',
      'file-read': 'bg-blue-100 text-blue-800',
      'file-write': 'bg-orange-100 text-orange-800',
      'file-download': 'bg-cyan-100 text-cyan-800',
      'file-upload': 'bg-indigo-100 text-indigo-800',
      'reverse-shell': 'bg-pink-100 text-pink-800',
      'bind-shell': 'bg-yellow-100 text-yellow-800',
      'capabilities': 'bg-gray-100 text-gray-800',
      'limited-suid': 'bg-amber-100 text-amber-800',
      'library-load': 'bg-emerald-100 text-emerald-800',
      'command': 'bg-slate-100 text-slate-800'
    };

    return typeColors[functionType] || 'bg-gray-100 text-gray-800';
  };

  const getPrivilegeBadges = (func: GTFOBinsFunction) => {
    const badges = [];
    if (func.sudo) badges.push({ text: 'Sudo', color: 'bg-red-100 text-red-800' });
    if (func.suid) badges.push({ text: 'SUID', color: 'bg-purple-100 text-purple-800' });
    if (func.capabilities) badges.push({ text: 'Capabilities', color: 'bg-gray-100 text-gray-800' });
    if (func.limited_suid) badges.push({ text: 'Limited SUID', color: 'bg-amber-100 text-amber-800' });
    return badges;
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
        </div>
      </div>
    );
  }

  if (error || !binary) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Error</h1>
          <p className="text-gray-600 mb-4">{error || "Binary not found"}</p>
          <Button onClick={() => navigate('/toolkits/gtfobins')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to GTFOBins
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-[#e1dcdc75] relative">
      {/* Navigation bar */}
      <div className="fixed w-full px-4 top-[46px] z-10">
        <div className="max-w-[1257px] mx-auto h-[77px] relative rounded-[28px]">
          <div className="absolute w-full h-full top-0 left-0 bg-white rounded-[28px] shadow-[0px_4px_4px_#00000040] blur-[3.05px] opacity-[0.53]" />
          <div className="relative h-full flex items-center justify-between px-6">
            <div className="flex items-center gap-4 cursor-pointer" onClick={() => navigate('/toolkits')}>
              <img className="w-[27px] h-[27px] object-cover" alt="Beetle" src="/beetle.png" />
              <span className="font-['Space_Grotesk'] font-bold text-black text-[32px]">RAWPA</span>
            </div>
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                onClick={() => navigate('/toolkits')}
                className="font-['Space_Grotesk'] text-black"
              >
                Back to Toolkits
              </Button>
              <div 
                onClick={handleSignOut}
                className="w-[48px] h-[48px] bg-[#daed6e] rounded-[12px] flex items-center justify-center cursor-pointer hover:bg-[#c5d85c] transition-colors"
              >
                <img className="w-6 h-6" alt="User" src="/user-icon.svg" />
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Main content */}
      <div className="container mx-auto px-4 pt-[160px] pb-12">
        <div className="bg-white rounded-[20px] p-8 shadow-lg">
          {/* Header */}
          <div className="mb-8">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/toolkits/gtfobins')}
              className="mb-4"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to GTFOBins
            </Button>
            
            <div className="flex justify-between items-start">
              <div>
                <h1 className="text-4xl font-bold text-gray-900 mb-2">{binary.name}</h1>
                <p className="text-gray-600 text-lg mb-4">
                  {binary.description || `${binary.name} binary for privilege escalation`}
                </p>
                <div className="flex flex-wrap gap-2">
                  {binary.functions.map((func, index) => (
                    <Badge 
                      key={index} 
                      className={getFunctionTypeBadge(func.type)}
                    >
                      {func.type}
                    </Badge>
                  ))}
                </div>
              </div>
              
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => window.open(binary.url, '_blank')}
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  View on GTFOBins
                </Button>
              </div>
            </div>
          </div>

          {/* Functions */}
          <div className="space-y-6">
            {binary.functions.map((func, index) => (
              <Card key={index} className="border-l-4 border-l-blue-500">
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="text-xl font-semibold capitalize">
                        {func.name.replace('-', ' ')}
                      </CardTitle>
                      <p className="text-gray-600 mt-1">{func.description}</p>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {getPrivilegeBadges(func).map((badge, badgeIndex) => (
                        <Badge key={badgeIndex} className={badge.color}>
                          {badge.text}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </CardHeader>
                
                <CardContent>
                  {func.code && (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm font-medium text-gray-700">Command</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => copyToClipboard(func.code)}
                        >
                          <Copy className="w-4 h-4 mr-1" />
                          Copy
                        </Button>
                      </div>
                      <pre className="text-sm text-gray-800 whitespace-pre-wrap font-mono bg-white p-3 rounded border">
                        {func.code}
                      </pre>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Footer */}
          <div className="mt-12 pt-8 border-t border-gray-200">
            <div className="text-center text-gray-600">
              <p>Data sourced from <a href="https://gtfobins.github.io" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">GTFOBins</a></p>
              <p className="text-sm mt-1">License: GPL-3.0</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}; 